/**
 * Markup de la galería de screenshots — Fase 10d.
 *
 * Layout:
 *  - Header con título y, si hay sesión, botón SUBIR FOTOS a la
 *    derecha. Si no hay sesión, en su lugar un texto sutil
 *    "Inicia sesión para subir".
 *  - Grid de thumbnails con la imagen, caption (si la hay) y meta
 *    (autor + fecha). Click sobre una abre el lightbox.
 *
 * El grid usa CSS Grid auto-fill con minmax para responsividad sin
 * media queries.
 */

import type { GalleryPhoto } from '@51esc/shared';

/**
 * Render del shell de la galería. La estructura está pensada para
 * encajar sobre la hoja de `Reporte_fondo.png` que viene como skin
 * por defecto del chrome (`.chrome-bg-wood`). El `.gal-paper` se
 * posiciona en absoluto cubriendo el área visible de la hoja en el
 * PNG; las thumbnails viven dentro con grid + scroll vertical.
 *
 * El botón de subir vive en el sidebar ACCIONES (no aquí), por
 * coherencia con el resto de páginas del chrome (operaciones, notams).
 */
export function renderGalleryShell(): string {
  return `
    <div class="gal-stage">
      <div class="gal-paper">
        <header class="gal-header">
          <h1 class="gal-title">GALERÍA</h1>
          <p class="gal-subtitle">Screenshots del 51 Escuadrón</p>
        </header>

        <div class="gal-grid" data-zone="grid"></div>
        <p class="gal-empty" data-zone="empty" hidden>
          — Todavía no hay fotos en la galería. —
        </p>
      </div>
    </div>
  `;
}

/**
 * Sidebar ACCIONES de la galería. Solo si el caller está autenticado;
 * para anónimos dejamos el sidebar por defecto "— sin acciones —"
 * (mountChrome con allowAnonymous ya ofrece INICIAR SESIÓN en el
 * sidebar izquierdo).
 */
export function renderGaleriaActions(canUpload: boolean): string {
  if (!canUpload) {
    return `
      <section class="chrome-actions">
        <h2 class="chrome-actions-title">ACCIONES</h2>
        <p class="chrome-actions-empty">
          Inicia sesión para<br />subir fotos.
        </p>
      </section>
    `;
  }
  return `
    <section class="chrome-actions gal-actions">
      <h2 class="chrome-actions-title">ACCIONES</h2>
      <ul class="gal-actions-list">
        <li>
          <button
            type="button"
            class="gal-action-btn"
            data-gal-action="upload"
          >+ SUBIR FOTOS</button>
        </li>
      </ul>
    </section>
  `;
}

export function renderGrid(photos: GalleryPhoto[]): string {
  return photos.map(renderThumb).join('');
}

function renderThumb(p: GalleryPhoto): string {
  return `
    <button
      type="button"
      class="gal-thumb"
      data-photo-id="${escapeHtml(p.id)}"
      data-photo-author="${escapeHtml(p.uploadedBy ?? '')}"
      aria-label="${escapeHtml(p.caption ?? 'Ver foto')}"
    >
      <img
        class="gal-thumb-img"
        src="${escapeHtml(p.url)}"
        alt="${escapeHtml(p.caption ?? '')}"
        loading="lazy"
      />
      ${p.caption
        ? `<span class="gal-thumb-caption">${escapeHtml(p.caption)}</span>`
        : ''}
      <span class="gal-thumb-meta">
        ${escapeHtml(p.authorCallsign)} · ${formatDate(p.createdAt)}
      </span>
    </button>
  `;
}

export function renderError(message: string): string {
  return `<div class="gal-error">${escapeHtml(message)}</div>`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
